import * as mutations from '../../graphql/mutations';
import * as queries from '../../graphql/queries';
import { STANDARD_ROLES, SystemType, Permissions } from './roleConstants';
import { isSystemRole } from './roleUtils';
import { graphqlQuery } from '@/appsync/service/GraphqlUtils';
import { commonModule } from '@/store/modules/CommonStore';

export interface Role {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  systemType: SystemType;
  tenant: string;
  landscape: string;
  permissions: Permissions;
  createdAt?: string;
  updatedAt?: string;
}

export interface RoleData {
  id?: string;
  name: string;
  description?: string;
  isDefault: boolean;
  systemType: SystemType;
  tenant: string;
  landscape: string;
  permissions: Permissions;
}

type RawRole = Omit<Role, 'permissions'> & { permissions: string };

/**
 * 新しいロールを作成する
 * @param {Object} roleData ロールデータ（tenant, landscapeを含む）
 * @returns {Promise<Object>} 作成されたロール
 */
export async function createRole(roleData: RoleData): Promise<any> {
  const createdAt = new Date().toISOString();
  const input = {
    ...roleData,
    permissions: JSON.stringify(roleData.permissions),
    createdAt,
    updatedAt: createdAt,
  };

  // デフォルトロールの場合、同一テナント・ランドスケープ内の既存デフォルトロールを無効にする
  if (roleData.isDefault) {
    await clearExistingDefaultRole(
      roleData.systemType,
      roleData.tenant,
      roleData.landscape
    );
  }

  const result = await graphqlQuery<
    { createRole: any },
    { input: typeof input }
  >(mutations.createRole, { input });
  return result ? result.createRole : null;
}

/**
 * ロールを更新する
 * @param {Object} roleData 更新するロールデータ
 * @returns {Promise<Object>} 更新されたロール
 */
export async function updateRole(roleData: RoleData): Promise<any> {
  const input = {
    id: roleData.id,
    name: roleData.name,
    description: roleData.description,
    isDefault: roleData.isDefault,
    systemType: roleData.systemType,
    tenant: roleData.tenant,
    landscape: roleData.landscape,
    permissions: JSON.stringify(roleData.permissions),
    updatedAt: new Date().toISOString(),
  };

  // デフォルトロールの場合、同一テナント・ランドスケープ内の他のデフォルトロールを無効にする
  if (roleData.isDefault) {
    await clearExistingDefaultRole(
      roleData.systemType,
      roleData.tenant,
      roleData.landscape,
      roleData.id // 現在更新中のロールIDは除外
    );
  }

  const result = await graphqlQuery<
    { updateRole: any },
    { input: typeof input }
  >(mutations.updateRole, { input });
  return result ? result.updateRole : null;
}

/**
 * IDでロールを取得する
 * @param {string} id ロールID
 * @returns {Promise<Object>} 取得したロール
 */
export async function getRole(id: string): Promise<Role | null> {
  const result = await graphqlQuery<{ getRole: RawRole }, { id: string }>(
    queries.getRole,
    { id }
  );

  const role = result ? result.getRole : null;
  if (!role) {
    return null;
  }

  // permissions が存在しない場合はデフォルト値を設定
  const permissions = role.permissions
    ? JSON.parse(role.permissions)
    : STANDARD_ROLES.ENABLE_ALL.permissions;

  return {
    ...role,
    permissions,
  };
}

/**
 * 全ロールを取得する
 * @param {string} tenant テナント（必須）
 * @param {string} landscape ランドスケープ（必須）
 * @param {Object} filter フィルター条件
 * @returns {Promise<Array>} ロールの配列
 */
export async function listRoles(
  tenant: string,
  landscape: string,
  filter: any = null
): Promise<Role[]> {
  // テナント・ランドスケープでフィルタリング
  const variables = {
    tenant,
    landscape: { eq: landscape },
    filter,
  };
  const result = await graphqlQuery<
    { roleByTenantLandscape: { items: RawRole[] } },
    typeof variables
  >(queries.roleByTenantLandscape, variables);

  const roles =
    result && result.roleByTenantLandscape && result.roleByTenantLandscape.items
      ? result.roleByTenantLandscape.items
      : [];
  return roles.map(role => ({
    ...role,
    permissions: JSON.parse(role.permissions),
  }));
}

/**
 * 既存のデフォルトロールを取得する
 * @param {string} systemType システムタイプ (FBS/PORTAL)
 * @param {string} tenant テナント
 * @param {string} landscape ランドスケープ
 * @param {string} excludeRoleId 除外するロールID（更新時の自分自身など）
 * @returns {Promise<Role|null>} 既存のデフォルトロール（存在する場合）
 */
export async function findExistingDefaultRole(
  systemType: SystemType,
  tenant: string,
  landscape: string,
  excludeRoleId: string | null = null
): Promise<Role | null> {
  try {
    const filter = {
      isDefault: { eq: true },
      systemType: { eq: systemType },
    };

    const existingRoles = await listRoles(tenant, landscape, filter);

    // 除外するロールIDがある場合はフィルタリング
    const filteredRoles = excludeRoleId
      ? existingRoles.filter(role => role.id !== excludeRoleId)
      : existingRoles;

    // 最初に見つかったデフォルトロールを返す
    return filteredRoles.length > 0 ? filteredRoles[0] : null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('既存デフォルトロールの検索に失敗しました:', error);
    return null;
  }
}

/**
 * ユーザーのロールを更新する
 * @param {string} loginName ログイン名
 * @param {string} tenant テナント
 * @param {string} landscape ランドスケープ
 * @param {string} roleId ロールID
 * @returns {Promise<Object>} 更新されたユーザー
 */
export async function assignRoleToUser(
  loginName: string,
  tenant: string,
  landscape: string,
  roleId: string
): Promise<any> {
  try {
    const input = {
      loginName: loginName,
      tenant: tenant,
      landscape: landscape,
      roleId: roleId,
    };

    const result = await graphqlQuery<
      { updateUser: any },
      { input: typeof input }
    >(mutations.updateUser, { input });
    return result ? result.updateUser : null;
  } catch (error) {
    // エラーの詳細をログに出力（一時的にデバッグ用）
    throw new Error(
      `assignRoleToUser failed: ${JSON.stringify(error, null, 2)}`
    );
  }
}

/**
 * ユーザーのロールを取得する
 * @param {string} userId ユーザーID
 * @returns {Promise<Object>} ユーザーのロール
 */
export async function getUserRole(userId: string): Promise<Role | null> {
  const result = await graphqlQuery<
    { getUser: { role: RawRole } },
    { id: string }
  >(queries.getUser, { id: userId });

  const user = result && result.getUser;
  if (!user || !user.role) {
    return null;
  }

  // permissions が存在しない場合はデフォルト値を設定
  const permissions = user.role.permissions
    ? JSON.parse(user.role.permissions)
    : STANDARD_ROLES.ENABLE_ALL.permissions;

  return {
    ...user.role,
    permissions,
  };
}

/**
 * 既存のデフォルトロールを無効にする
 * @param {string} systemType システムタイプ (FBS/PORTAL)
 * @param {string} tenant テナント
 * @param {string} landscape ランドスケープ
 * @param {string} excludeRoleId 除外するロールID（更新時の自分自身など）
 */
async function clearExistingDefaultRole(
  systemType: SystemType,
  tenant: string,
  landscape: string,
  excludeRoleId: string | null = null
) {
  try {
    const filter = {
      isDefault: { eq: true },
      systemType: { eq: systemType },
    };

    const existingRoles = await listRoles(tenant, landscape, filter);

    // 除外するロールIDがある場合はフィルタリング
    const rolesToUpdate = excludeRoleId
      ? existingRoles.filter(role => role.id !== excludeRoleId)
      : existingRoles;

    // 既存のデフォルトロールがある場合、isDefaultをfalseに設定
    for (const role of rolesToUpdate) {
      const updateInput = {
        id: role.id,
        name: role.name,
        description: role.description,
        isDefault: false, // デフォルトフラグを無効にする
        systemType: role.systemType,
        tenant: role.tenant,
        landscape: role.landscape,
        permissions: JSON.stringify(role.permissions),
        updatedAt: new Date().toISOString(),
      };

      await graphqlQuery<{ updateRole: any }, { input: typeof updateInput }>(
        mutations.updateRole,
        { input: updateInput }
      );
    }

    if (rolesToUpdate.length > 0) {
      // eslint-disable-next-line no-console
      console.log(
        `${rolesToUpdate.length}個の既存デフォルトロールを無効にしました`
      );
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('既存デフォルトロールの無効化に失敗しました:', error);
    throw error;
  }
}

/**
 * 指定されたロールに所属するユーザー数を取得する
 * @param {string} roleId ロールID
 * @param {string} tenant テナント
 * @param {string} landscape ランドスケープ
 * @returns {Promise<number>} 所属するユーザー数
 */
export async function countUsersInRole(
  roleId: string,
  tenant: string,
  landscape: string
): Promise<number> {
  type VariablesType = {
    tenant: string;
    landscape: { eq: string };
    filter: {
      roleId: { eq: string };
    };
    limit: number;
    nextToken: string | null;
  };

  type ResultType = {
    userByTenantLandscape: { items: any[]; nextToken: string | null };
  } | null;

  try {
    let count = 0;
    let nextToken: string | null = null;

    do {
      const variables: VariablesType = {
        tenant,
        landscape: { eq: landscape },
        filter: {
          roleId: { eq: roleId },
        },
        limit: 1000,
        nextToken,
      };

      const result: ResultType = await graphqlQuery<
        { userByTenantLandscape: { items: any[]; nextToken: string | null } },
        VariablesType
      >(queries.userByTenantLandscape, variables);

      if (
        result &&
        result.userByTenantLandscape &&
        result.userByTenantLandscape.items
      ) {
        count += result.userByTenantLandscape.items.length;
        nextToken = result.userByTenantLandscape.nextToken;
      } else {
        nextToken = null;
      }
    } while (nextToken);

    return count;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('ロールに所属するユーザー数の取得に失敗しました:', error);
    throw error;
  }
}

/**
 * ロールを削除する
 * @param {string} id 削除するロールのID
 * @returns {Promise<Object>} 削除されたロール情報
 */
export async function deleteRole(id: string): Promise<any> {
  // システムロールは削除不可
  if (isSystemRole(id)) {
    throw new Error('システムロールは削除できません');
  }

  const input = { id };

  const result = await graphqlQuery<
    { deleteRole: any },
    { input: typeof input }
  >(mutations.deleteRole, { input });
  return result ? result.deleteRole : null;
}

/**
 * ユーザーの権限情報を取得する
 * @param {string} loginName ログイン名
 * @param {string} tenant テナント
 * @param {string} landscape ランドスケープ
 * @returns {Promise<Object>} ユーザーの権限オブジェクト
 */
export async function getUserRolePermissions(
  loginName: string,
  tenant: string,
  landscape: string
): Promise<Permissions> {
  try {
    // ユーザーのロール情報を取得
    const result = await graphqlQuery<
      { getUser: { roleId: string } },
      { loginName: string; tenant: string; landscape: string }
    >(queries.getUser, {
      loginName,
      tenant,
      landscape,
    });

    const user = result && result.getUser;

    if (!user || !user.roleId) {
      return STANDARD_ROLES.ENABLE_ALL.permissions;
    }

    // ユーザーに割り当てられたロールの権限を取得
    const role = await getRole(user.roleId);

    if (role && role.permissions) {
      return role.permissions;
    }

    // ロールが存在しない、または権限が設定されていない場合はデフォルト権限を返す
    return STANDARD_ROLES.ENABLE_ALL.permissions;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('権限情報の取得中にエラーが発生しました:', error);
    // エラー時はデフォルトで最も制限された権限を返す
    return {
      menus: {
        scheduler: false,
        job: false,
        settings: false,
        home: false,
        sitemap: false,
      },
    };
  }
}

/**
 * ログインユーザーが指定されたロールに所属しているかを確認する
 * @param {string} roleId 確認したいロールID
 * @returns {Promise<boolean>} 所属している場合は true
 */
export async function isUserMemberOfRole(roleId: string): Promise<boolean> {
  if (!roleId) {
    return false;
  }

  // commonModule からログイン情報を取得
  const { loginName, tenant, landscape } = commonModule.loginInfo;
  if (!loginName || !tenant || !landscape) {
    // ログイン情報がなければ確認できない
    return false;
  }

  try {
    // ログイン情報を使ってユーザーのロールIDを取得
    const result = await graphqlQuery<
      { getUser: { roleId: string } },
      { loginName: string; tenant: string; landscape: string }
    >(queries.getUser, {
      loginName,
      tenant,
      landscape,
    });

    const user = result && result.getUser;
    // ユーザーのロールIDと引数のロールIDを比較
    return !!(user && user.roleId === roleId);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('ユーザーのロール確認中にエラーが発生しました:', error);
    return false;
  }
}
